import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export const aiKeys = {
  all: ['ai'] as const,
  conversations: () => [...aiKeys.all, 'conversations'] as const,
  conversation: (id: number) => [...aiKeys.all, 'conversation', id] as const,
};

export interface AiToolCall {
  name: string;
  ok: boolean;
  rowCount: number | null;
}

export interface AiChatResponse {
  conversationId: number;
  answer: string;
  toolCalls: AiToolCall[];
  usage: { inputTokens: number; outputTokens: number };
  model: string | null;
}

export interface AiChatRequest {
  conversationId?: number | null;
  message: string;
}

export interface AiConversationSummary {
  id: number;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Ask ActiveID — non-streaming turn. POST /api/v2/ai/chat. */
export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: AiChatRequest) => {
      const response = await api.post<AiChatResponse>('/api/v2/ai/chat', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.conversations() });
    },
  });
}

/** The caller's own conversation threads (tenant + user scoped server-side). */
export function useConversations(enabled = true) {
  return useQuery({
    queryKey: aiKeys.conversations(),
    queryFn: async () => {
      const response = await api.get<{ items: AiConversationSummary[] }>('/api/v2/ai/conversations');
      return response.data.items;
    },
    enabled,
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/v2/ai/conversations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.conversations() });
    },
  });
}

// ── Phase 2 — Reconciliation Copilot ──────────────────────────────────────────
export type ReconcileAction = 'ACCEPT_LOCATION' | 'MARK_MISSING' | 'REVIEW';

export interface AiReconcileSuggestion {
  assetId: string;
  action: ReconcileAction;
  confidence: number;
  rationale: string;
}

/** Requests AI recommendations for a closed inventory count's discrepancy rows. */
export function useReconcileSuggestions() {
  return useMutation({
    mutationFn: async (processId: number) => {
      const response = await api.post<{ suggestions: AiReconcileSuggestion[] }>(
        `/api/v2/ai/reconcile/${processId}/suggest`,
        {},
      );
      return response.data.suggestions;
    },
  });
}
